/**
 * Manages the image gallery UI
 */
export class GalleryManager {
    constructor(imageManager) {
        this.imageManager = imageManager;
        this.isOpen = false;

        // DOM elements
        this.panel = null;
        this.thumbnails = null;
        this.toggleButton = null;

        // Pre-created thumbnail elements (imageId -> HTMLElement)
        this.thumbnailElements = new Map();

        // Callbacks
        this.onImageSelected = null;
    }

    /**
     * Initialize the gallery with DOM elements
     * @param {HTMLElement} panel - Gallery panel element
     * @param {HTMLElement} thumbnails - Thumbnails container element
     * @param {HTMLElement} toggleButton - Toggle button element
     */
    initialize(panel, thumbnails, toggleButton) {
        this.panel = panel;
        this.thumbnails = thumbnails;
        this.toggleButton = toggleButton;

        // Setup toggle button
        if (this.toggleButton) {
            this.toggleButton.addEventListener('click', () => this.toggle());
        }

        // Setup close button
        const closeButton = document.getElementById('gallery-close-btn');
        if (closeButton) {
            closeButton.addEventListener('click', () => this.hide());
        }

        console.log(`✅ Gallery initialized`);
    }

    /**
     * Toggle gallery visibility
     */
    toggle() {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.show();
        } else {
            this.hide();
        }
    }

    /**
     * Show the gallery
     */
    show() {
        if (!this.panel) return;

        const t0 = performance.now();

        this.panel.classList.remove('hidden');
        const t1 = performance.now();
        console.log(`[PERF] Remove hidden class: ${(t1 - t0).toFixed(2)}ms`);

        this.panel.classList.add('translate-y-0');
        this.panel.classList.remove('translate-y-full');
        const t2 = performance.now();
        console.log(`[PERF] Add transition classes: ${(t2 - t1).toFixed(2)}ms`);

        // Hide toggle button when gallery is open
        if (this.toggleButton) {
            this.toggleButton.classList.add('hidden');
        }

        this.updateThumbnails();
        const t3 = performance.now();
        console.log(`[PERF] updateThumbnails: ${(t3 - t2).toFixed(2)}ms`);

        console.log(`🖼️ Gallery opened - TOTAL: ${(t3 - t0).toFixed(2)}ms`);
    }

    /**
     * Hide the gallery
     */
    hide() {
        if (!this.panel) return;

        this.isOpen = false;

        this.panel.classList.add('translate-y-full');
        this.panel.classList.remove('translate-y-0');

        // Show toggle button when gallery is closed
        if (this.toggleButton) {
            this.toggleButton.classList.remove('hidden');
        }

        setTimeout(() => {
            this.panel.classList.add('hidden');
        }, 300);

        console.log('🖼️ Gallery closed');
    }

    /**
     * Update gallery thumbnails (create on-the-fly if needed)
     */
    updateThumbnails() {
        if (!this.thumbnails) return;

        const t0 = performance.now();

        const images = this.imageManager.getAllImages();
        const currentIndex = this.imageManager.currentImageIndex;

        // Check if all thumbnails are in the Map (they should be pre-created)
        const allThumbnailsReady = images.every(image => this.thumbnailElements.has(image.id));

        // Check if DOM needs rebuilding (different count OR thumbnails not in DOM yet)
        const thumbnailsInDOM = Array.from(this.thumbnails.children).filter(
            child => child.classList.contains('gallery-thumbnail-container')
        ).length;
        const needsRebuild = thumbnailsInDOM !== images.length || !allThumbnailsReady;

        const t1 = performance.now();
        console.log(`[PERF] updateThumbnails prep: ${(t1 - t0).toFixed(2)}ms, needsRebuild=${needsRebuild}, children=${this.thumbnails.children.length}, images=${images.length}`);

        if (needsRebuild) {
            // Use DocumentFragment for batch DOM insertion (faster)
            const fragment = document.createDocumentFragment();

            const t2 = performance.now();

            // Create or reuse thumbnail elements
            images.forEach((image, index) => {
                let thumbnailContainer = this.thumbnailElements.get(image.id);

                // Create element if it doesn't exist (fallback, should be pre-created)
                if (!thumbnailContainer) {
                    this.createLowResThumbnail(image);
                    thumbnailContainer = this.thumbnailElements.get(image.id);
                    if (!thumbnailContainer) {
                        // Still loading, skip for now
                        return;
                    }
                }

                // Update active class
                if (index === currentIndex) {
                    thumbnailContainer.classList.add('active');
                } else {
                    thumbnailContainer.classList.remove('active');
                }

                // Update click handler with current index
                thumbnailContainer.onclick = () => {
                    if (this.onImageSelected) {
                        this.onImageSelected(index);
                    }
                };

                fragment.appendChild(thumbnailContainer);
            });

            const t3 = performance.now();
            console.log(`[PERF] Create/update elements in fragment: ${(t3 - t2).toFixed(2)}ms`);

            // Single DOM update
            this.thumbnails.innerHTML = '';
            const t4 = performance.now();
            console.log(`[PERF] Clear innerHTML: ${(t4 - t3).toFixed(2)}ms`);

            this.thumbnails.appendChild(fragment);
            const t5 = performance.now();
            console.log(`[PERF] appendChild(fragment): ${(t5 - t4).toFixed(2)}ms`);

            console.log(`🖼️ Thumbnails rebuilt: ${images.length} images - TOTAL: ${(t5 - t0).toFixed(2)}ms`);
        } else {
            // Just update active states (no DOM manipulation)
            images.forEach((image, index) => {
                const thumbnailContainer = this.thumbnailElements.get(image.id);
                if (thumbnailContainer) {
                    if (index === currentIndex) {
                        thumbnailContainer.classList.add('active');
                    } else {
                        thumbnailContainer.classList.remove('active');
                    }
                }
            });

            const t2 = performance.now();
            console.log(`🖼️ Thumbnails active state updated - TOTAL: ${(t2 - t0).toFixed(2)}ms`);
        }
    }

    /**
     * Create a low-resolution thumbnail for an image
     * @param {Object} image - Image data
     * @returns {Promise} Resolves when thumbnail is created
     */
    createLowResThumbnail(image) {
        if (this.thumbnailElements.has(image.id)) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            // Check if we have a pre-generated thumbnail URL (WebP format)
            if (image.thumbnail_url) {
                // console.log(`🖼️ Using pre-generated thumbnail for ${image.name}: ${image.thumbnail_url}`);

                // Create thumbnail container directly from URL
                const container = document.createElement('div');
                container.className = 'gallery-thumbnail-container';

                const img = document.createElement('img');
                img.alt = image.name;
                img.className = 'gallery-thumbnail';
                img.crossOrigin = 'anonymous';

                // Wait for image to load before resolving
                img.onload = () => {
                    // console.log(`✅ Pre-generated thumbnail loaded for ${image.name}`);
                    resolve();
                };

                img.onerror = (error) => {
                    console.warn(`❌ Failed to load pre-generated thumbnail for ${image.name}:`, error);
                    console.warn(`   URL was: ${image.thumbnail_url}`);
                    // Remove the failed container
                    this.thumbnailElements.delete(image.id);
                    // Fallback to dynamic generation
                    console.log(`   Falling back to dynamic generation...`);
                    image.thumbnail_url = null; // Disable thumbnail_url for this image
                    this.createLowResThumbnail(image).then(resolve);
                };

                const nameLabel = document.createElement('div');
                nameLabel.className = 'gallery-thumbnail-name';
                nameLabel.textContent = image.name;
                nameLabel.title = image.name; // Full name on hover

                container.appendChild(img);
                container.appendChild(nameLabel);

                this.thumbnailElements.set(image.id, container);

                // Set src AFTER setting up event handlers
                img.src = image.thumbnail_url;
                return;
            }

            // Fallback: No thumbnail_url provided
            console.error(`❌ Missing thumbnail_url for image: ${image.name}`);
            console.error(`   Image URL: ${image.url}`);
            console.error(`   Please ensure your API returns a 'thumbnail_url' field for all images`);

            // Create a placeholder container (no dynamic generation needed)
            const container = document.createElement('div');
            container.className = 'gallery-thumbnail-container';

            const placeholder = document.createElement('div');
            placeholder.className = 'gallery-thumbnail bg-slate-700 flex items-center justify-center text-slate-400 text-xs';
            placeholder.textContent = 'No thumbnail';
            placeholder.style.minHeight = '100px';

            const nameLabel = document.createElement('div');
            nameLabel.className = 'gallery-thumbnail-name';
            nameLabel.textContent = image.name;
            nameLabel.title = image.name;

            container.appendChild(placeholder);
            container.appendChild(nameLabel);

            this.thumbnailElements.set(image.id, container);
            resolve();
        });
    }

    /**
     * Wait for all thumbnails to be created
     * @returns {Promise} Resolves when all thumbnails are ready
     */
    async waitForThumbnails() {
        const images = this.imageManager.getAllImages();
        const promises = images.map(image => {
            if (!this.thumbnailElements.has(image.id)) {
                return this.createLowResThumbnail(image);
            }
            return Promise.resolve();
        });
        await Promise.all(promises);
        console.log(`✅ All ${images.length} thumbnails ready`);
    }

    /**
     * Refresh gallery if open
     */
    refresh() {
        if (this.isOpen) {
            this.updateThumbnails();
        }
    }

    /**
     * Check if gallery is open
     * @returns {boolean}
     */
    isGalleryOpen() {
        return this.isOpen;
    }
}
